import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { DocumentOwnerTypeSchema, ExpiringDocumentSchema } from '@taqon/contracts';
import { z } from 'zod';
import { getData, getPage } from '../../shared/api/client';
import { ExpiryHint } from '../../shared/documents/DocumentsPanel';
import { businessToday } from '../../shared/documents/dates';
import { recordPath, viewCapability } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';

const PAGE_SIZE = 50;
const PRESETS = [7, 30, 60, 90] as const;

/**
 * Expiring documents (`/documents/expiring`; FRD-HRP-017, FRD-PRC-011): available documents of
 * expiry-tracked types that expire within the chosen window, and those already expired, across
 * every record the session may see. The API decides visibility per owning record; a row links to
 * its record's page only where that page exists and the session may open it.
 */
export function ExpiringDocumentsPage() {
  const session = useSession();
  const [withinDays, setWithinDays] = useState<number>(30);
  const [daysText, setDaysText] = useState('30');
  const [recordType, setRecordType] = useState('');
  const [page, setPage] = useState(1);

  const ownerTypes = useQuery({
    queryKey: ['documents', 'owner-types'],
    queryFn: () => getData('/api/v1/documents/owner-types', z.array(DocumentOwnerTypeSchema)),
  });
  const expiring = useQuery({
    queryKey: ['documents', 'expiring', withinDays, recordType, page],
    queryFn: () =>
      getPage(
        `/api/v1/documents/expiring?withinDays=${String(withinDays)}&page=${String(page)}` +
          `&pageSize=${String(PAGE_SIZE)}` +
          (recordType === '' ? '' : `&recordType=${encodeURIComponent(recordType)}`),
        ExpiringDocumentSchema,
      ),
  });
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  const nameOf = (code: string) =>
    ownerTypes.data?.find((type) => type.code === code)?.displayName ?? code;
  const total = expiring.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const choose = (days: number) => {
    setWithinDays(days);
    setDaysText(String(days));
    setPage(1);
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Expiring documents"
        description="Documents with a tracked expiry date that expire within the window, and those already expired."
      />
      <SearchFilterBar aria-label="Filters">
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {PRESETS.map((days) => (
            <Button
              key={days}
              size="small"
              variant={withinDays === days ? 'contained' : 'outlined'}
              aria-pressed={withinDays === days}
              onClick={() => {
                choose(days);
              }}
            >
              {days} days
            </Button>
          ))}
        </Stack>
        <TextField
          size="small"
          label="Within days"
          type="number"
          value={daysText}
          onChange={(e) => {
            setDaysText(e.target.value);
            const value = Number(e.target.value);
            if (e.target.value !== '' && Number.isInteger(value) && value >= 0 && value <= 3650) {
              setWithinDays(value);
              setPage(1);
            }
          }}
          slotProps={{ htmlInput: { min: 0, max: 3650 } }}
        />
        <TextField
          select
          size="small"
          label="Record type"
          value={recordType}
          onChange={(e) => {
            setRecordType(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">All record types</MenuItem>
          {(ownerTypes.data ?? []).map((type) => (
            <MenuItem key={type.code} value={type.code}>
              {type.displayName}
            </MenuItem>
          ))}
        </TextField>
      </SearchFilterBar>
      {expiring.isError && <ErrorState message={expiring.error.message} />}
      {expiring.isPending && <LoadingState label="Loading expiring documents" />}
      {expiring.data !== undefined && (
        <Section title="Documents">
          {expiring.data.items.length === 0 ? (
            <EmptyState
              title="Nothing expiring"
              detail={`No document you may see expires within ${String(withinDays)} days.`}
            />
          ) : (
            <>
              <Table size="small" aria-label="Expiring documents">
                <TableHead>
                  <TableRow>
                    <TableCell>Expiry</TableCell>
                    <TableCell>Record</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Number</TableCell>
                    <TableCell>Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expiring.data.items.map((document) => {
                    const path = recordPath(document.ownerRecordType, document.ownerRecordId);
                    const label =
                      document.ownerLabel ??
                      `${nameOf(document.ownerRecordType)} #${document.ownerRecordId}`;
                    return (
                      <TableRow key={document.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {document.expiryDate}
                          {document.expiryDate !== null && (
                            <ExpiryHint expiryDate={document.expiryDate} today={today} />
                          )}
                        </TableCell>
                        <TableCell>
                          {path !== null &&
                          hasCapability(session.data, viewCapability(document.ownerRecordType)) ? (
                            <RecordLink to={path}>{label}</RecordLink>
                          ) : (
                            label
                          )}
                          <Typography variant="caption" component="div" color="text.secondary">
                            {nameOf(document.ownerRecordType)}
                          </Typography>
                        </TableCell>
                        <TableCell>{document.documentTypeName}</TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{document.title}</TableCell>
                        <TableCell>{document.documentNumber ?? '—'}</TableCell>
                        <TableCell>
                          <Link
                            href={`/api/v1/documents/${document.id}/content`}
                            aria-label={`Download ${document.title}`}
                          >
                            Download
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Button
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((p) => p - 1);
                  }}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {page} of {lastPage} — {total} documents
                </Typography>
                <Button
                  disabled={page >= lastPage}
                  onClick={() => {
                    setPage((p) => p + 1);
                  }}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </Section>
      )}
    </Stack>
  );
}
